// Data layer for the monitoring section.
//
// Live mode (default): react-query hooks call the Alliance backend
// (/api/monitoring/*, see docs/migration-plan.md in the solar-dashboard repo)
// through the shared axios client (withCredentials + 401→refresh retry).
//
// Mock mode (REACT_APP_MONITORING_MOCK=1): the same payload shapes are
// composed locally from mockData.js - the deterministic port of the reference
// dashboard's mock - so the section is fully usable before the backend lands.
//
// Payload contract (mirrors solarData.ts outputs + freshness markers):
//   summary:  { city, statusLabel, statusOk, currentKw|null, systemKwp,
//               installDate (ISO), tariffRsPerKwh, systemCostRs|null, as_of, stale,
//               forecast: { tomorrowKwh, condition, note, as_of } | null,
//               pr: { todayKwhActual, todayKwhExpected, ratioPct, label,
//                     disclaimer } | null,
//               performance: { state: "better"|"usual"|"worse"|null,
//                              ratioPct, cleaningNudge, streakDays } | null }
//   day:      { kwh, curve[24], expectedCurve[24]|null (today only,
//               weather-adjusted), peakKw, peakTime, savedRs, sunHours, co2Kg,
//               percentile, outages: OutageEvent[], as_of, stale }
//   month:    { kwh, savedRs, co2Kg, percentile, days, bestDay,
//               outages: MonthOutageSummary, as_of, stale }
//   year:     { kwh, savedRs, co2Kg, percentile, months, bestMonth,
//               outages: YearOutageSummary, as_of, stale }
//   lifetime: { kwh, co2Kg, systemCostRs, source, as_of, stale }

import { api } from "@/lib/api";
import {
  getDayData,
  getDayOutages,
  getMonthData,
  getMonthOutages,
  getYearData,
  getYearOutages,
  INSTALL_DATE,
  LIFETIME,
  SYSTEM_COST_RS,
  SYSTEM_KWP,
  TARIFF_RS_PER_KWH,
} from "./mockData";

export const MONITORING_MOCK = process.env.REACT_APP_MONITORING_MOCK === "1";

const dayParam = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Admin plant selector: when set, every request addresses that plant; unset
// means the backend falls back to the caller's own / first active plant.
const plantParam = (plantId) => (plantId ? { plant_id: plantId } : {});

/* ── live fetchers (backend contract) ───────────────────────── */

const live = {
  summary: async (plantId) => {
    const d = (await api.get("/monitoring/summary", { params: plantParam(plantId) })).data;
    // Normalize the backend summary into the flat shape the section expects
    // (same shape the mock composes).
    return {
      city: d.plant?.location || "",
      plantName: d.plant?.name || "",
      plantId: d.plant?.id ?? null,
      statusLabel: d.status?.label ?? "All good",
      statusOk: d.status?.ok ?? true,
      currentKw: d.currentKw ?? null,
      currentKwHeld: d.currentKwHeld ?? false,
      currentKwOffline: d.currentKwOffline ?? false,
      systemKwp: d.plant?.systemKwp,
      installDate: d.plant?.installDate,
      inverterModel: d.plant?.inverterModel ?? null,
      inverterBrand: d.plant?.inverterBrand ?? null,
      dataloggerPn: d.plant?.dataloggerPn ?? null,
      vendorCapacityKw: d.plant?.vendorCapacityKw ?? null,
      plantStatus: d.plant?.plantStatus ?? null,
      tariffRsPerKwh: d.plant?.tariffRsPerKwh,
      systemCostRs: d.plant?.systemCostRs ?? null,
      onboarding: d.plant?.onboarding ?? null,
      account: d.plant?.account ?? null,
      forecast: d.forecast ?? null,
      forecastStatus: d.forecast_status ?? null,
      pr: d.pr ?? null,
      performance: d.performance ?? null,
      as_of: d.as_of ?? null,
      stale: d.stale ?? false,
    };
  },
  day: async (date, _tariff, plantId) =>
    (await api.get("/monitoring/day", { params: { date: dayParam(date), ...plantParam(plantId) } }))
      .data,
  month: async (year, month, _tariff, plantId) =>
    // Backend takes a 0-based month (solarData.ts contract), same as the
    // caller's JS Date#getMonth - no offset here.
    (await api.get("/monitoring/month", { params: { year, month, ...plantParam(plantId) } })).data,
  year: async (year, _tariff, plantId) =>
    (await api.get("/monitoring/year", { params: { year, ...plantParam(plantId) } })).data,
  lifetime: async (plantId) => {
    const d = (await api.get("/monitoring/lifetime", { params: plantParam(plantId) })).data;
    return { ...d, source: d.source ?? "app" };
  },
  saveTariff: async (tariff, plantId) =>
    (
      await api.put("/monitoring/settings/tariff", {
        tariff_rs_per_kwh: tariff,
        // Both settings PUTs resolve the plant from the request BODY -
        // a query param is silently ignored and the edit hits the default plant.
        ...(plantId ? { plant_id: plantId } : {}),
      })
    ).data,
  plants: async () => (await api.get("/monitoring/plants")).data,
  // Owner-editable plant metadata; patch keys are the backend's snake_case
  // field names (system_kwp / install_date / location / inverter_model / name).
  savePlantSettings: async (patch, plantId) =>
    (
      await api.put("/monitoring/settings/plant", {
        ...patch,
        ...(plantId ? { plant_id: plantId } : {}),
      })
    ).data,
};

/* ── mock fetchers (same shapes, composed from mockData) ────── */

const fresh = () => ({ as_of: new Date().toISOString(), stale: false });

const mock = {
  summary: async () => ({
    city: "Rajkot",
    statusLabel: "All good",
    statusOk: true,
    currentKw: null, // mock has no live power - the UI shows the demo value
    systemKwp: SYSTEM_KWP,
    installDate: INSTALL_DATE.toISOString(),
    tariffRsPerKwh: TARIFF_RS_PER_KWH,
    // Plausible weather-adjusted verdict so the performance chip demos its
    // three states (live mode computes this server-side).
    performance: { state: "usual", ratioPct: 102, cleaningNudge: false, streakDays: 0 },
    ...fresh(),
  }),
  day: async (date, tariff) => ({
    ...getDayData(date, tariff),
    outages: getDayOutages(date, tariff),
    ...fresh(),
  }),
  month: async (year, month, tariff) => ({
    ...getMonthData(year, month, tariff),
    outages: getMonthOutages(year, month, tariff),
    ...fresh(),
  }),
  year: async (year, tariff) => ({
    ...getYearData(year, tariff),
    outages: getYearOutages(year, tariff),
    ...fresh(),
  }),
  lifetime: async () => ({
    kwh: LIFETIME.kwh,
    co2Kg: LIFETIME.co2Kg,
    systemCostRs: SYSTEM_COST_RS,
    source: "app",
    ...fresh(),
  }),
  saveTariff: async (tariff) => ({ tariff_rs_per_kwh: tariff }),
  // The selector is hidden in mock mode - this only exists for shape parity.
  plants: async () => [],
  // Mock mode has no persistence - the panel stays read-only there, so this
  // only exists to keep the source interface symmetric.
  savePlantSettings: async (patch) => patch,
};

const source = MONITORING_MOCK ? mock : live;

export const fetchSummary = (plantId) => source.summary(plantId);
export const fetchDay = (date, tariff, plantId) => source.day(date, tariff, plantId);
export const fetchMonth = (year, month, tariff, plantId) =>
  source.month(year, month, tariff, plantId);
export const fetchYear = (year, tariff, plantId) => source.year(year, tariff, plantId);
export const fetchLifetime = (plantId) => source.lifetime(plantId);
export const saveTariff = (tariff, plantId) => source.saveTariff(tariff, plantId);
export const fetchPlants = () => source.plants();
export const savePlantSettings = (patch, plantId) => source.savePlantSettings(patch, plantId);
